import { useState, useRef, useEffect, useCallback } from 'react';
import { apiFetch } from '../../hooks/useApi.js';

function SendIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="22" y1="2" x2="11" y2="13" />
      <polygon points="22 2 15 22 11 13 2 9 22 2" />
    </svg>
  );
}

function SparkleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  );
}

// ── Confirmation card ─────────────────────────────────────────────────────────
function ConfirmationCard({ action, onConfirm, onReject }) {
  return (
    <div className="confirm-card">
      <div className="confirm-header">
        <SparkleIcon />
        <span>AI wants to perform an action</span>
      </div>
      <div className="confirm-action">
        <strong>{action.description || action.type}</strong>
        {action.details && (
          <pre className="confirm-details">{JSON.stringify(action.details, null, 2)}</pre>
        )}
      </div>
      <div className="confirm-btns">
        <button className="btn-reject" onClick={onReject}>Cancel</button>
        <button className="btn-confirm" onClick={onConfirm}>Confirm</button>
      </div>
    </div>
  );
}

// ── Message bubble ────────────────────────────────────────────────────────────
function Message({ msg }) {
  const isUser = msg.role === 'user';
  return (
    <div className={`ai-msg ${isUser ? 'ai-msg--user' : 'ai-msg--assistant'}`}>
      {!isUser && (
        <div className="ai-msg-avatar"><SparkleIcon /></div>
      )}
      <div className="ai-msg-bubble">
        {msg.content}
      </div>
    </div>
  );
}

// ── Main panel ────────────────────────────────────────────────────────────────
export default function AIPanel({ open, onClose }) {
  const [messages, setMessages] = useState([{
    role: 'assistant',
    content: 'Hi — I\'m the Blake assistant. I can help with stock queries, production scheduling, HMRC figures, traceability, and more. What do you need?',
  }]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [pendingLogId, setPendingLogId] = useState(null);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 100);
  }, [open]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const send = useCallback(async (text) => {
    if (!text.trim() || loading) return;
    const userMsg = { role: 'user', content: text.trim() };
    setMessages(m => [...m, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const res = await apiFetch('/ai/chat', {
        method: 'POST',
        body: { message: text.trim(), history: messages.slice(-10) },
      });

      const aiMsg = { role: 'assistant', content: res.text || '(no response)' };
      setMessages(m => [...m, aiMsg]);

      // If the AI proposes a write action, surface confirmation card
      if (res.pending_action) {
        setPendingAction(res.pending_action);
        setPendingLogId(res.log_id);
      }
    } catch (err) {
      setMessages(m => [...m, { role: 'assistant', content: `Error: ${err.message}` }]);
    } finally {
      setLoading(false);
    }
  }, [messages, loading]);

  const handleConfirm = useCallback(async () => {
    if (!pendingAction) return;
    setLoading(true);
    try {
      await apiFetch('/ai/confirm', {
        method: 'POST',
        body: { log_id: pendingLogId },
      });
      setMessages(m => [...m, { role: 'assistant', content: '✓ Action completed.' }]);
    } catch (err) {
      setMessages(m => [...m, { role: 'assistant', content: `Action failed: ${err.message}` }]);
    } finally {
      setPendingAction(null);
      setPendingLogId(null);
      setLoading(false);
    }
  }, [pendingAction, pendingLogId]);

  if (!open) return null;

  return (
    <>
      {/* Backdrop — click outside to close */}
      <div className="ai-backdrop" onClick={onClose} />

      <div className="ai-panel">
        {/* Header */}
        <div className="ai-panel-header">
          <div className="ai-panel-title">
            <SparkleIcon />
            <span>AI Assistant</span>
          </div>
          <button className="ai-panel-close" onClick={onClose}>✕</button>
        </div>

        {/* Messages */}
        <div className="ai-messages">
          {messages.map((m, i) => <Message key={i} msg={m} />)}
          {loading && (
            <div className="ai-msg ai-msg--assistant">
              <div className="ai-msg-avatar"><SparkleIcon /></div>
              <div className="ai-msg-bubble ai-thinking">
                <span /><span /><span />
              </div>
            </div>
          )}
          {pendingAction && (
            <ConfirmationCard
              action={pendingAction}
              onConfirm={handleConfirm}
              onReject={() => { setPendingAction(null); setPendingLogId(null); }}
            />
          )}
          <div ref={bottomRef} />
        </div>

        {/* Input */}
        <div className="ai-input-bar">
          <input
            ref={inputRef}
            className="ai-input"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(input); } }}
            placeholder="Ask anything — stock, production, HMRC, SALSA…"
            disabled={loading}
          />
          <button
            className="ai-send-btn"
            onClick={() => send(input)}
            disabled={!input.trim() || loading}
            aria-label="Send"
          >
            <SendIcon />
          </button>
        </div>
      </div>

      <style>{`
        .ai-backdrop {
          position: fixed;
          inset: 0;
          z-index: 198;
          /* transparent — panel slides over, doesn't squeeze */
        }

        .ai-panel {
          position: fixed;
          top: 0;
          right: 0;
          bottom: 0;
          width: 400px;
          max-width: 95vw;
          background: var(--surface);
          z-index: 200;
          display: flex;
          flex-direction: column;
          box-shadow: -4px 0 24px rgba(0,0,0,.12);
          animation: slideIn .18s ease;
        }

        @keyframes slideIn {
          from { transform: translateX(100%); }
          to   { transform: translateX(0); }
        }

        .ai-panel-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0 18px;
          height: var(--header-height);
          border-bottom: 1px solid var(--border);
          flex-shrink: 0;
          background: var(--pld-green);
          color: #fff;
        }

        .ai-panel-title {
          display: flex;
          align-items: center;
          gap: 8px;
          font-weight: 600;
          font-size: 14px;
        }

        .ai-panel-close {
          color: rgba(255,255,255,0.7);
          font-size: 16px;
          padding: 4px 8px;
          border-radius: var(--radius-sm);
          transition: color var(--transition);
        }
        .ai-panel-close:hover { color: #fff; }

        .ai-messages {
          flex: 1;
          overflow-y: auto;
          padding: 16px;
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .ai-msg {
          display: flex;
          gap: 10px;
          max-width: 92%;
        }

        .ai-msg--user {
          align-self: flex-end;
          flex-direction: row-reverse;
        }

        .ai-msg-avatar {
          width: 28px;
          height: 28px;
          border-radius: 50%;
          background: rgba(34,57,31,.1);
          color: var(--pld-green);
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .ai-msg-bubble {
          background: var(--surface-2);
          padding: 10px 14px;
          border-radius: var(--radius-lg);
          font-size: 13.5px;
          line-height: 1.5;
          white-space: pre-wrap;
          word-break: break-word;
        }

        .ai-msg--user .ai-msg-bubble {
          background: var(--pld-green);
          color: #fff;
          border-bottom-right-radius: 4px;
        }

        .ai-msg--assistant .ai-msg-bubble {
          border-bottom-left-radius: 4px;
        }

        /* Typing indicator */
        .ai-thinking {
          display: flex;
          align-items: center;
          gap: 5px;
          padding: 12px 14px;
        }
        .ai-thinking span {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          background: var(--text-light);
          animation: bounce .9s ease infinite;
          display: inline-block;
        }
        .ai-thinking span:nth-child(2) { animation-delay: .15s; }
        .ai-thinking span:nth-child(3) { animation-delay: .3s; }
        @keyframes bounce {
          0%, 60%, 100% { transform: translateY(0); }
          30% { transform: translateY(-6px); }
        }

        /* Confirmation card */
        .confirm-card {
          border: 1px solid var(--pld-yellow);
          border-radius: var(--radius-lg);
          background: #fffbe0;
          padding: 16px;
          margin: 4px 0;
        }
        .confirm-header {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 12px;
          font-weight: 600;
          color: var(--pld-green);
          margin-bottom: 10px;
        }
        .confirm-action {
          font-size: 13.5px;
          margin-bottom: 12px;
        }
        .confirm-details {
          font-size: 11px;
          background: #fff;
          border: 1px solid var(--border);
          border-radius: var(--radius-sm);
          padding: 8px;
          margin-top: 8px;
          overflow-x: auto;
          white-space: pre-wrap;
          word-break: break-word;
          max-height: 120px;
        }
        .confirm-btns {
          display: flex;
          gap: 8px;
          justify-content: flex-end;
        }
        .btn-reject {
          padding: 6px 14px;
          border-radius: var(--radius-sm);
          border: 1px solid var(--border);
          font-size: 13px;
          color: var(--text-muted);
        }
        .btn-reject:hover { border-color: var(--status-critical); color: var(--status-critical); }
        .btn-confirm {
          padding: 6px 16px;
          border-radius: var(--radius-sm);
          background: var(--pld-green);
          color: #fff;
          font-size: 13px;
          font-weight: 600;
        }
        .btn-confirm:hover { background: var(--pld-green-dark); }

        /* Input */
        .ai-input-bar {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 16px;
          border-top: 1px solid var(--border);
          flex-shrink: 0;
        }

        .ai-input {
          flex: 1;
          border-radius: var(--radius);
          padding: 8px 12px;
          font-size: 13.5px;
          background: var(--surface-2);
          border-color: transparent;
        }
        .ai-input:focus { border-color: var(--pld-green); background: var(--surface); }

        .ai-send-btn {
          width: 36px;
          height: 36px;
          border-radius: var(--radius);
          background: var(--pld-green);
          color: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: background var(--transition), opacity var(--transition);
        }
        .ai-send-btn:disabled { opacity: 0.4; cursor: not-allowed; }
        .ai-send-btn:not(:disabled):hover { background: var(--pld-green-dark); }
      `}</style>
    </>
  );
}
